// useCurrentMenu.js
import { computed, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useApiMenu } from '@/composables/useApiMenu'
import { useMenuStore } from '@/store/menu.store'

/**
 * menuType === 'DIR'인 경우, 하위 메뉴 중 menuLink가 있는 첫 번째 메뉴를 찾음
 */
function findFirstActualMenu(menu, allMenus) {
  if (!menu) return null
  
  // menuLink가 있고 'N' 또는 'dir'이 아니면 현재 메뉴가 실제 페이지
  if (menu.menuLink && menu.menuLink !== 'N' && menu.menuLink !== 'dir') {
    return menu
  }
  
  // menuType이 'DIR'이면 하위 메뉴 찾기
  if (menu.menuType === 'DIR' || menu.menuType === 'dir') {
    const childMenus = allMenus.filter(m => m.upMenuId === menu.menuId)
    for (const child of childMenus) {
      const found = findFirstActualMenu(child, allMenus)
      if (found) return found
    }
  }
  
  return null
}

export function useCurrentMenu() {
  const route = useRoute()
  const { menusWithLevel, getMenuBreadcrumb } = useApiMenu()
  const menuStore = useMenuStore()

  const shouldRun = computed(() => {
    const layout = route.meta?.layout || 'default'
    return layout !== 'blank'
  })

  // 쿼리에서 menuId 가져오기
  const menuIdFromQuery = computed(() => {
    const q = route.query?.menuId
    return Array.isArray(q) ? q[0] : q || null
  })

  const currentMenu = computed(() => {
    if (!shouldRun.value) return null

    const menus = menusWithLevel.value || []
    if (!menus.length) return null

    // 1) query.menuId 최우선
    if (menuIdFromQuery.value) {
      let byQuery = menus.find(m => m.menuId === menuIdFromQuery.value)
      if (byQuery) {
        // DIR 타입이면 실제 페이지 메뉴 찾기
        if (byQuery.menuType === 'DIR') {
          const actualMenu = findFirstActualMenu(byQuery, menus)
          if (actualMenu) {
            byQuery = actualMenu
          }
        }
        // store와 싱크
        if (menuStore.currentMenuId !== byQuery.menuId) {
          menuStore.setCurrentMenu(byQuery.menuId)
        }
        return byQuery
      }
    }

    // 1-1) fromBoardAttr='1' localStorage나 쿼리가 있으면 게시판속성관리 메뉴 우선
    const fromBoardAttr = localStorage.getItem('fromBoardAttr') || route.query?.fromBoardAttr
    if (fromBoardAttr && String(fromBoardAttr) === '1') {
      const boardAttrMenu = menus.find(m => {
        const menuPath = m.path || m.menuLink
        return menuPath && menuPath.includes('board-attr')
      })
      if (boardAttrMenu) {
        if (menuStore.currentMenuId !== boardAttrMenu.menuId) {
          menuStore.setCurrentMenu(boardAttrMenu.menuId)
        }
        return boardAttrMenu
      }
    }

    // 2) store.currentMenuId (버튼 클릭 등에서 저장된 값)
    if (menuStore.currentMenuId) {
      let byId = menus.find(m => m.menuId === menuStore.currentMenuId)
      if (byId) {
        // DIR 타입이면 실제 페이지 메뉴 찾기
        if (byId.menuType === 'DIR') {
          const actualMenu = findFirstActualMenu(byId, menus)
          if (actualMenu) {
            byId = actualMenu
          }
        }
        
        const menuPath = byId.path || byId.menuLink
        if (menuPath && menuPath !== 'dir' && menuPath !== 'N') {
          const normalizedMenuPath = menuPath.replace(/\/$/, '')
          const normalizedRoutePath = route.path.replace(/\/$/, '')
          if (
            normalizedRoutePath.startsWith(normalizedMenuPath) ||
            normalizedMenuPath === normalizedRoutePath
          ) {
            return byId
          }
        }
      }
    }

    // 3) fallback: path 기준
    const path = route.path
    if (!path || menus.length === 0) return null

    let exact =
      menus.find(m => m.path === path || m.menuLink === path) || null
    if (exact) return exact

    const routeSegs = path.split('/').filter(Boolean)
    let bestMenu = null
    let bestScore = -1

    menus.forEach(m => {
      const menuPath = m.path || m.menuLink
      if (!menuPath) return

      const menuSegs = menuPath.split('/').filter(Boolean)

      let i = 0
      while (
        i < routeSegs.length &&
        i < menuSegs.length &&
        routeSegs[i] === menuSegs[i]
      ) {
        i++
      }

      let score = i
      const menuLast = menuSegs[menuSegs.length - 1]
      if (menuLast && routeSegs.includes(menuLast)) {
        score += 0.5
      }

      if (score > bestScore) {
        bestScore = score
        bestMenu = m
      }
    })

    // dev logging removed

    return bestMenu
  })

  const currentTitle = computed(
    () => currentMenu.value?.menuNm || ''
  )

  const currentBreadcrumbs = computed(() => {
    if (!currentMenu.value) return []
    return getMenuBreadcrumb(currentMenu.value.menuId)
  })

  // route 바뀔 때도 query.menuId를 우선으로 store에 반영
  watch(
    () => [route.path, route.query.menuId, route.query.fromBoardAttr],
    () => {
      const menus = menusWithLevel.value || []
      if (!menus.length) return

      // 1) query.menuId 우선
      if (menuIdFromQuery.value) {
        let byQuery = menus.find(m => m.menuId === menuIdFromQuery.value)
        if (byQuery) {
          // DIR 타입이면 실제 페이지 메뉴 찾기
          if (byQuery.menuType === 'DIR') {
            const actualMenu = findFirstActualMenu(byQuery, menus)
            if (actualMenu) {
              byQuery = actualMenu
            }
          }
          menuStore.setCurrentMenu(byQuery.menuId)
          return
        }
      }

      // 1-1) fromBoardAttr='1' localStorage나 쿼리가 있으면 게시판속성관리 메뉴 우선
      const fromBoardAttr = localStorage.getItem('fromBoardAttr') || route.query?.fromBoardAttr
      if (fromBoardAttr && String(fromBoardAttr) === '1') {
        const boardAttrMenu = menus.find(m => {
          const menuPath = m.path || m.menuLink
          return menuPath && menuPath.includes('board-attr')
        })
        if (boardAttrMenu) {
          menuStore.setCurrentMenu(boardAttrMenu.menuId)
          return
        }
      }

      // 2) query 없으면 path 기준으로 자동 매칭
      const path = route.path
      const exact = menus.find(
        m =>
          (m.path === path || m.menuLink === path) &&
          m.menuType !== 'DIR' &&
          m.path !== 'dir' &&
          m.menuLink !== 'dir' &&
          m.menuLink !== 'N'
      )

      if (exact) {
        menuStore.setCurrentMenu(exact.menuId)
        return
      }

      const routeSegs = path.split('/').filter(Boolean)
      let bestMenu = null
      let bestScore = -1

      menus.forEach(m => {
        const menuPath = m.path || m.menuLink
        if (!menuPath || menuPath === 'dir' || menuPath === 'N' || m.menuType === 'DIR') return

        const menuSegs = menuPath.split('/').filter(Boolean)

        let i = 0
        while (
          i < routeSegs.length &&
          i < menuSegs.length &&
          routeSegs[i] === menuSegs[i]
        ) {
          i++
        }

        let score = i
        const menuLast = menuSegs[menuSegs.length - 1]
        if (menuLast && routeSegs.includes(menuLast)) {
          score += 0.5
        }

        if (score > bestScore) {
          bestScore = score
          bestMenu = m
        }
      })

      if (bestMenu && bestScore > 0) {
        menuStore.setCurrentMenu(bestMenu.menuId)
      }
    }
  )

  return {
    currentMenu,
    currentTitle,
    currentBreadcrumbs
  }
}
